import Events from '../constants/events';
import { siteApi } from '../services';
import {
  hasChanged,
  handleError,
  getCookie,
  setCookie,
  setItemWithExpiry,
  getItemWithExpiry,
} from '../utilities';
import type {
  ConsentRecord,
  AllowState,
  SetConsentParams,
  RefreshDataParams,
} from '@/types';

const defaultAllowState: AllowState = {
  analytics: false,
  do_not_sell: false,
  functional: false,
  limit_pii_use: false,
  marketing: false,
  strictly_necessary: true,
  unclassified: false,
};

class ConsentState {
  consentState: ConsentRecord[] | null;
  regionExpiration: number;
  regionId: string | null;
  gcmEnabled: boolean;
  allowState: AllowState | null;
  loading: boolean;
  defaultAllowState: AllowState;

  constructor() {
    this.consentState = null;
    this.regionExpiration = Math.floor(12 * 30.44 * 24 * 60 * 60); // 12 months in seconds
    this.regionId = null;
    this.gcmEnabled = false;
    this.allowState = null;
    this.loading = true;
    this.defaultAllowState = defaultAllowState;
  }

  updateGoogleConsent(): void {
    function gtag() {
      /* eslint-disable-next-line prefer-rest-params */
      window.dataLayer.push(arguments);
    }

    const consentModeStates = {
      ad_storage: this.allowState?.marketing ? 'granted' : 'denied',
      ad_user_data: this.allowState?.marketing ? 'granted' : 'denied',
      ad_personalization: this.allowState?.marketing ? 'granted' : 'denied',
      analytics_storage: this.allowState?.analytics ? 'granted' : 'denied',
      functionality_storage: this.allowState?.functional ? 'granted' : 'denied',
      personalization_storage: this.allowState?.functional
        ? 'granted'
        : 'denied',
      security_storage: 'granted',
    };

    // @ts-expect-error - Google gtag function signature
    gtag('set', 'developer_id.dM2I0MW', true);
    // @ts-expect-error - Google gtag function signature
    gtag('consent', 'update', consentModeStates);

    /**
     * Push consent updated event with state data to dataLayer for GTM. This is
     * useful as this event can be used in GTM to trigger tags that should run
     * after consent is updated.
     */
    window.dataLayer.push({
      event: 'consent_updated',
      consent_state: {
        analytics: this.allowState?.analytics ? 'granted' : 'denied',
        marketing: this.allowState?.marketing ? 'granted' : 'denied',
        functional: this.allowState?.functional ? 'granted' : 'denied',
      },
    });
  }

  getStoredConsentState(storedState: unknown): ConsentRecord[] | null {
    if (!storedState) return null;
    const state = storedState as { items?: ConsentRecord[] } | ConsentRecord[];
    if ('items' in state && Array.isArray(state.items)) {
      return state.items;
    }
    if (Array.isArray(state)) {
      return state;
    }
    return null;
  }

  hasState(id: string): ConsentRecord | undefined {
    return this.consentState?.find(({ consentType }) => consentType.id === id);
  }

  computeAllowState(): AllowState {
    const privacyConsents = this.consentState
      ? this.consentState.filter((rec) => rec.category === 'privacy_setting')
      : [];
    if (privacyConsents.length > 0) {
      const allowState = privacyConsents.reduce(
        (a, { subcategory, consentState: state }) => ({
          ...a,
          [subcategory]: state === 'accepted',
        }),
        {} as Partial<AllowState>,
      );
      return { ...(this.allowState || defaultAllowState), ...allowState };
    }
    return this.allowState || defaultAllowState;
  }

  syncCookie(): void {
    setCookie({
      name: `concord-allow-state-${this.regionId}`,
      value: JSON.stringify(this.allowState),
      maxAge: this.regionExpiration,
    });
  }

  setConsent({
    regionId,
    regionExpiration,
    gcmEnabled,
  }: SetConsentParams): void {
    this.regionId = regionId;
    this.regionExpiration = regionExpiration;
    this.gcmEnabled = gcmEnabled;

    /* Try to get allow state from cookie first */
    const cookieValue = getCookie({
      name: `concord-allow-state-${this.regionId}`,
    });

    let parsedCookieValue;
    try {
      parsedCookieValue = cookieValue ? JSON.parse(cookieValue) : null;
      /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    } catch (e) {
      parsedCookieValue = null;
    }

    /* Try to get full consent state from localStorage */
    const storedState = getItemWithExpiry({
      key: `concord-consent-state-${this.regionId}`,
    });

    const consentState = this.getStoredConsentState(storedState);

    if (parsedCookieValue && Object.keys(parsedCookieValue).length) {
      /* Use cookie value if it exists */
      this.allowState = parsedCookieValue;
      if (
        consentState &&
        Array.isArray(consentState) &&
        consentState.length > 0
      ) {
        this.consentState = consentState;
      }
    } else if (
      consentState &&
      Array.isArray(consentState) &&
      consentState.length > 0
    ) {
      /* Use localStorage if cookie not present but localStorage exists */
      this.consentState = consentState;
      this.allowState = this.computeAllowState();
      this.syncCookie();
    } else {
      /* Use defaults if neither exists */
      this.allowState = defaultAllowState;
      this.consentState = [];
      this.syncCookie();
    }

    /* Update Google consent if enabled */
    if (this.gcmEnabled) {
      if (window.dataLayer) {
        this.updateGoogleConsent();
      } else {
        // Poll for dataLayer availability with max retries
        let retryCount = 0;
        const maxRetries = 5; // 500ms total (5 * 100ms)

        const checkDataLayer = () => {
          if (window.dataLayer) {
            this.updateGoogleConsent();
          } else if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(checkDataLayer, 100);
          } else {
            console.warn(
              'dataLayer not available after maximum retries, skipping Google Consent Mode update',
            );
          }
        };
        checkDataLayer();
      }
    }
  }

  async refreshData({
    forceRefresh = false,
  }: RefreshDataParams = {}): Promise<void> {
    try {
      /* Skip API call if we have valid local state and it isn't a force refresh */
      if (!forceRefresh && this.consentState && this.consentState.length > 0) {
        return;
      }

      this.loading = true;
      window.dispatchEvent(new CustomEvent(Events.ConsentStateLoading));

      const results = await siteApi.getConsentState();
      const originalData = this.consentState;
      this.consentState = results.items;

      if (results.items?.length) {
        /* Store in localStorage */
        setItemWithExpiry({
          key: `concord-consent-state-${this.regionId}`,
          value: { items: results.items },
          expirySeconds: this.regionExpiration,
        });

        if (hasChanged(results.items, originalData)) {
          const newAllowState = this.computeAllowState();
          if (hasChanged(newAllowState, this.allowState)) {
            this.allowState = newAllowState;
            this.syncCookie();
            /* Update the Google consent settings if GCM is enabled. */
            if (this.gcmEnabled) {
              if (window.dataLayer) {
                this.updateGoogleConsent();
              } else {
                // Poll for dataLayer availability with max retries
                let retryCount = 0;
                const maxRetries = 5; // 500ms total (5 * 100ms)

                const checkDataLayer = () => {
                  if (window.dataLayer) {
                    this.updateGoogleConsent();
                  } else if (retryCount < maxRetries) {
                    retryCount++;
                    setTimeout(checkDataLayer, 100);
                  } else {
                    console.warn(
                      'dataLayer not available after maximum retries, skipping Google Consent Mode update',
                    );
                  }
                };
                checkDataLayer();
              }
            }

            window.dispatchEvent(new CustomEvent(Events.ConsentStateChanged));
          }
        }
      }

      window.dispatchEvent(new CustomEvent(Events.ConsentStateLoaded));
    } catch (err) {
      handleError(err as Error);
    } finally {
      this.loading = false;
    }
  }
}

export const consentState = new ConsentState();
